import { useState, useCallback } from 'react';
// @ts-expect-error CJS/ESM interop
import { useImperativeFilePicker } from 'use-file-picker';
// @ts-expect-error CJS/ESM interop
import { FileAmountLimitValidator, FileSizeValidator, FileTypeValidator } from 'use-file-picker/validators';
import { isIOS, bytesToMB, showToastMessage } from '../utils/AppUtils';
import captureFrame from '../utils/captureFrame';
import { base64ToFileWithDetails } from '../utils/mediaHelper';
import { SentryCapture } from '../analytics/Sentry';
import { useSelector } from 'react-redux';
import { mediaControlConfigurationSelector } from '../selectors/selectors';
import { useThemeContext } from './useThemeManager';
import { en_config } from '../config';

interface UseMediaUploaderOptions {
    onFileParsed: (args: {
        file: File;
        preview: string;
        metadata?: {
            duration: number;
            width: number;
            height: number;
            type: string;
        };
        featureImageFile?: File;
        isImage: boolean;
    }) => void;
    accept?: string[];
}

export function useMediaUploader({ onFileParsed, accept = ['video/*', 'image/*'] }: UseMediaUploaderOptions) {
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);
    const { device } = useThemeContext();

    const globalMediaControl = useSelector(mediaControlConfigurationSelector);
    const deviceUploadLimit = device.isMobile
        ? (globalMediaControl.mobile_size_limit as number)
        : (globalMediaControl.file_size_limit as number);
    const imageFileLimit = globalMediaControl?.image_size_limit ?? en_config.MEDIA_CONFIG.image_size_limit;

    const { openFilePicker, clear } = useImperativeFilePicker({
        multiple: false,
        readFilesContent: true,
        accept,
        readAs: 'DataURL',
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(['mov', 'mp4', 'jpg', 'jpeg', 'png', 'webm']),
            new FileSizeValidator({ maxFileSize: globalMediaControl.file_size_limit }),
        ],
        onFilesRejected: ({ errors }) => {
            const error = errors[0];
            switch (error.name) {
                case 'FileAmountLimitError':
                    showToastMessage('Only one file allowed.', 'info');
                    break;
                case 'FileSizeError':
                    const file = error.causedByFile;
                    const sizeLimit = file.type.startsWith('image/')
                        ? globalMediaControl.image_size_limit!
                        : globalMediaControl.file_size_limit;
                    showToastMessage(`File too large. Max size is ${bytesToMB(sizeLimit)} MB.`, 'error');
                    break;
                case 'FileReaderError':
                    showToastMessage('File unreadable. Please check your file.', 'info');
                    break;
                case 'FileTypeError':
                    showToastMessage('Unsupported format. Allowed: .mov, .mp4, .jpg, .png, .webm', 'info');
                    break;
                default:
                    showToastMessage('File error occurred.', 'error');
                    break;
            }
        },
        onFilesSuccessfullySelected: async ({ plainFiles }) => {
            const file = plainFiles[0];
            try {
                if (isIOS && file.size === 0) {
                    showToastMessage('Downloading from iCloud. Please wait...', 'info', 10000);
                    let attempts = 0;
                    const maxAttempts = 30;
                    const pollFile = () => {
                        if (file.size > 0) return processFile(file);
                        if (++attempts >= maxAttempts) {
                            showToastMessage('File download timed out.', 'error');
                            return;
                        }
                        setTimeout(pollFile, 1000);
                    };
                    pollFile();
                } else {
                    processFile(file);
                }
            } catch (error) {
                SentryCapture(error);
                showToastMessage('An error occurred processing the file.', 'error');
            }
        },
    });

    const processFile = async (file: File) => {
        try {
            setLoading(true);

            if (file.type.startsWith('image/')) {
                const imageData = URL.createObjectURL(file);
                setPreview(imageData);
                onFileParsed({
                    file,
                    preview: imageData,
                    isImage: true,
                });
                setLoading(false);
                return;
            }

            if (file.type.startsWith('video/')) {
                const videoEl = document.createElement('video');
                videoEl.preload = 'metadata';
                videoEl.src = URL.createObjectURL(file);

                videoEl.addEventListener('loadedmetadata', async () => {
                    const metadata = {
                        duration: videoEl.duration,
                        width: videoEl.videoWidth,
                        height: videoEl.videoHeight,
                        type: file.type,
                    };

                    try {
                        const frameData = await captureFrame(file, 3);
                        const featureImageFile = base64ToFileWithDetails(frameData).file;

                        setPreview(frameData);

                        onFileParsed({
                            file,
                            preview: frameData,
                            metadata,
                            featureImageFile,
                            isImage: false,
                        });
                    } catch (err) {
                        showToastMessage('Failed to capture video thumbnail.', 'error');
                    }

                    setLoading(false);
                });

                videoEl.addEventListener('error', () => {
                    setLoading(false);
                    showToastMessage('Error loading video metadata.', 'error');
                });
            }
        } catch (err) {
            setLoading(false);
            SentryCapture(err);
            showToastMessage('Error during file processing.', 'error');
        }
    };

    const clearSelection = useCallback(() => {
        clear();
        setPreview(null);
    }, [clear]);

    return {
        openFilePicker,
        loading,
        preview,
        clearSelection,
        deviceUploadLimit,
        imageFileLimit,
    };
}
